import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import FetchModule from '../../Share/Network/FetchModule';
import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';

import LockerMemberSearchInput from '../../../Component/LockerComponent/LockerMemberSearchInput';
import CalenderInput from '../../../Component/LockerComponent/LockerCalenderInput';
import CountDaysInput from '../../../Component/LockerComponent/CountDaysInput';
import PaymentOptionCustomInput from '../../../MemberRegistration/Component/PaymentOptionCustomInput';
import PaymentOptionSettingSelectBox from '../../../MemberRegistration/Model/PaymentOptionSettingSelectBox';
import AddPaymentOptionModal from '../../../MemberRegistration/Model/AddPaymentOptionModal';

import WhiteTrashIcon from '../../../image/WhiteTrashIcon.png';

const LockerStartModal = forwardRef((props, ref) => {
  const { lockerData, exitClick, saveClick } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 결제수단 name은 캐노니컬 한국어로 유지 — 표시 번역은 PaymentOptionSettingSelectBox의 PAYMENT_NAME_MAP이 전담 //
  const DefaultPaymentOption = [{ id: 0, name: '현금' }, { id: 0, name: '카드' }, { id: 0, name: '포인트' }];

  const [isLoading, setIsLoading] = useState(false);
  const [isClickedAddPaymentOption, setIsClickedAddPaymentOption] = useState(false);
  const [approvalDate, setApprovalDate] = useState(moment());
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [dayCount, setDayCount] = useState(1);
  const [cardPrice] = useState(0);
  const [cashPrice] = useState(0);
  const [point] = useState(0);

  const [paymentOptions, setPaymentOptions] = useState([{ id: 0, name: '현금' }, { id: 0, name: '카드' }, { id: 0, name: '포인트' }]);
  const [selectedPaymentOptions, setSelectedPaymentOptions] = useState([{ id: 0, name: '현금', price: 0 }]);

  const MemberRef = useRef();
  const ApprovalCalenderRef = useRef();
  const StartCalenderRef = useRef();
  const EndCalenderRef = useRef();
  const PriceRef = useRef();

  // 외부에서 접근 가능한 메서드 정의 //
  useImperativeHandle(ref, () => ({
    getMemberData: () => {
      return MemberRef.current.getValue();
    },
    getStartDate: () => {
      return StartCalenderRef.current.getValue();
    },
    getEndDate: () => {
      return EndCalenderRef.current.getValue();
    },
    getPrice: () => {
      return PriceRef.current.getValue();
    },
    getCard: () => {
      return cardPrice;
    },
    getCash: () => {
      return cashPrice;
    },
    getPoint: () => {
      return point;
    }
  }));

  // Expected 상태 락커 기존 데이터 pre-fill //
  useEffect(() => {
    if (lockerData.state !== 'Expected') return;

    if (lockerData.startDate) setStartDate(moment(lockerData.startDate));
    if (lockerData.endDate) setEndDate(moment(lockerData.endDate));
    if (lockerData.approvalDate) setApprovalDate(moment(lockerData.approvalDate));

    if (lockerData.memberId && MemberRef.current) {
      MemberRef.current.setPrefilledMember({ memberId: lockerData.memberId, name: lockerData.userName });
    }

    if (lockerData.selectedPaymentOptions && lockerData.selectedPaymentOptions.length > 0) {
      setSelectedPaymentOptions(lockerData.selectedPaymentOptions.map(opt => ({
        ...opt,
        price: parseInt(opt.price) || 0
      })));
    }
    else {
      const opts = [];
      if (lockerData.cashPrice > 0) opts.push({ id: 0, name: '현금', price: lockerData.cashPrice });
      if (lockerData.cardPrice > 0) opts.push({ id: 0, name: '카드', price: lockerData.cardPrice });
      if (lockerData.point > 0) opts.push({ id: 0, name: '포인트', price: lockerData.point });
      if (opts.length > 0) setSelectedPaymentOptions(opts);
    }
  }, [lockerData])

  // 결제수단 데이터 불러오기 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('payment_option/', 'GET');

        if (responseData.status === 200) {
          let newPaymentOptions = DefaultPaymentOption.concat(responseData.data.paymentOptions);

          setPaymentOptions(newPaymentOptions);
        }
        else if (responseData.status === 401) history.push('/login');
      }
    )();
  }, [])

  // 시작일, 종료일 변경시 일수 계산 //
  useEffect(() => {
    if (startDate > endDate) {
      alert(t('main.locker.start.endDateBeforeStart'));
      setEndDate(startDate);
      setDayCount(1);
    }
    else {
      let calcEndDate = moment(endDate.format('YYYY-MM-DDT23:59:59'));
      let dayCount = calcEndDate.diff(startDate, 'days');

      if (dayCount > 0) {
        setDayCount(dayCount + 1);
      }
    }
  }, [startDate, endDate])

  // 캘린더 클릭 시 다른 캘린더 닫기 //
  function onClickCalender(className) {
    if (className === 'StartCalender') {
      EndCalenderRef.current.closeCalender();
      ApprovalCalenderRef.current.closeCalender();
    }
    else if (className === 'EndCalender') {
      StartCalenderRef.current.closeCalender();
      ApprovalCalenderRef.current.closeCalender();
    }
    else {
      StartCalenderRef.current.closeCalender();
      EndCalenderRef.current.closeCalender();
    }
  }

  // 적용 일수 변경시 종료일 계산 //
  function onSetDayCount(value) {
    if (value > 0) {
      setEndDate(startDate.clone().add(value - 1, 'days'));
    }
    else setEndDate(startDate);

    setDayCount(value);
  }

  // 결제수단 모달 내에서 결제수단 저장하기 클릭시 //
  function onClickSavePaymentOption(optionName) {
    if (optionName === '') {
      alert(t('main.locker.enterPaymentName'));
      return;
    }

    if (isLoading) {
      alert(t('main.locker.savingInProgress'));
      return;
    }

    setIsLoading(true);

    let requestData = {
      optionName: optionName
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('payment_option/save', 'POST', requestData);

        if (responseData.status === 200) {
          let newPaymentOptions = DefaultPaymentOption.concat(responseData.data.paymentOptions);

          setPaymentOptions(newPaymentOptions);
          setIsLoading(false);
          setIsClickedAddPaymentOption(false);
        }
        else if (responseData.status === 401) history.push('/login');
        else {
          alert(responseData.error);
          setIsLoading(false);
        }
      }
    )();
  }

  // 결제 수단 삭제버튼 클릭시 //
  function onClickDeleteOption(paymentOptionId) {
    if (isLoading) {
      alert(t('main.locker.deletingInProgress'));
      return;
    }

    setIsLoading(true);

    let requestData = {
      paymentOptionId: paymentOptionId
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('payment_option/delete', 'POST', requestData);

        if (responseData.status === 200) {
          let newPaymentOptions = DefaultPaymentOption.concat(responseData.data.paymentOptions);

          setPaymentOptions(newPaymentOptions);
          setIsLoading(false);
          setIsClickedAddPaymentOption(false);
        }
        else if (responseData.status === 401) history.push('/login');
        else {
          alert(responseData.error);
          setIsLoading(false);
        }
      }
    )();
  }

  // 회원권 결제 수단 변경시 //
  function onChangeSelectedPaymentOptions(data, index) {
    let newSelectedPaymentOptions = selectedPaymentOptions.slice();

    newSelectedPaymentOptions[index].id = data.id;
    newSelectedPaymentOptions[index].name = data.name;

    setSelectedPaymentOptions(newSelectedPaymentOptions);
  }

  // 회원권 결제 금액 변경시 //
  function onChangePaymentOptionPrice(value, index) {
    let newSelectedPaymentOptions = selectedPaymentOptions.slice();

    newSelectedPaymentOptions[index].price = value.replaceAll(',', '');

    setSelectedPaymentOptions(newSelectedPaymentOptions);
  }

  // 회원권 결제 수단 추가 버튼 클릭시 //
  function onClickAddPaymentOptions() {
    let newSelectedPaymentOptions = selectedPaymentOptions.slice();

    newSelectedPaymentOptions.push({ id: 0, name: '현금', price: 0 });

    setSelectedPaymentOptions(newSelectedPaymentOptions);
  }

  // 회원권 결제 수단 제거 버튼 클릭시 //
  function onClickDeletePaymentOptions(index) {
    let newSelectedPaymentOptions = selectedPaymentOptions.slice();

    newSelectedPaymentOptions.splice(index, 1);

    setSelectedPaymentOptions(newSelectedPaymentOptions);
  }

  // 결제 옵션 추가 버튼 클릭시 //
  function onClickAdditionOption() {
    setIsClickedAddPaymentOption(true);
  }

  const isLinkoPaySelected = selectedPaymentOptions.some(opt => opt.name === '링코페이');

  // 락커 사용 시작 저장 클릭시 //
  function onClickSave() {
      let storeId = parseInt(window.sessionStorage.getItem('StoreId'));
      let lockerId = lockerData.id;
      let memberId = MemberRef.current.getValue().memberId;
      let finalCashPrice = 0;
      let finalCardPrice = 0;
      let finalPoint = 0;

      let finalSelectedPaymentOptions = [];

      for (let i = 0; i < selectedPaymentOptions.length; i++) {
        if (selectedPaymentOptions[i].price !== ''
          && selectedPaymentOptions[i].price !== 0) finalSelectedPaymentOptions.push({
            ...selectedPaymentOptions[i],
            price: parseInt(selectedPaymentOptions[i].price.toString().replaceAll(',', ''))
          });
      }

      for (let i = 0; i < finalSelectedPaymentOptions.length; i++) {
        if (finalSelectedPaymentOptions[i].id === 0) {
          if (finalSelectedPaymentOptions[i].name === '현금') finalCashPrice += parseInt(finalSelectedPaymentOptions[i].price.toString().replaceAll(',', ''));
          else if (finalSelectedPaymentOptions[i].name === '카드' || finalSelectedPaymentOptions[i].name === '링코페이') finalCardPrice += parseInt(finalSelectedPaymentOptions[i].price.toString().replaceAll(',', ''));
          else if (finalSelectedPaymentOptions[i].name === '포인트') finalPoint += parseInt(finalSelectedPaymentOptions[i].price.toString().replaceAll(',', ''));
        }
      }

      if (startDate === '') {
        alert(t('main.locker.selectStartDate'));
        return;
      }
      else if (endDate === '') {
        alert(t('main.locker.selectEndDate'));
        return;
      }
      else if (approvalDate === '') {
        alert(t('main.locker.start.selectPaymentDate'));
        return;
      }
      else if (memberId === 0 || memberId === undefined) {
        alert(t('main.locker.selectMember'));
        return;
      }

      let checkStartDate = moment(startDate.format('YYYY-MM-DD') + 'T00:00:00');
      let checkEndDate = moment(endDate.format('YYYY-MM-DD') + 'T23:59:59');

      if (checkEndDate.isBefore(checkStartDate)) {
        alert(t('main.locker.start.invalidEndDate'));
        return;
      }

      const fetchModule = new FetchModule();

      if (lockerData.state === 'Expected') {
        for (let i = 0; i < finalSelectedPaymentOptions.length; i++) {
          if (finalSelectedPaymentOptions[i].paymentOptionId === undefined) {
            finalSelectedPaymentOptions[i].paymentOptionId = finalSelectedPaymentOptions[i].id;
            finalSelectedPaymentOptions[i].id = 0;
          }
          finalSelectedPaymentOptions[i].paymentOptionName = finalSelectedPaymentOptions[i].name;
        }

        finalCashPrice = 0;
        finalCardPrice = 0;
        finalPoint = 0;
        for (let i = 0; i < finalSelectedPaymentOptions.length; i++) {
          if (finalSelectedPaymentOptions[i].paymentOptionId === 0) {
            const price = parseInt(finalSelectedPaymentOptions[i].price.toString().replaceAll(',', ''));

            if (finalSelectedPaymentOptions[i].name === '현금') finalCashPrice += price;
            else if (finalSelectedPaymentOptions[i].name === '카드' || finalSelectedPaymentOptions[i].name === '링코페이') finalCardPrice += price;
            else if (finalSelectedPaymentOptions[i].name === '포인트') finalPoint += price;
          }
        }

        const originalMerchantPart = lockerData.merchantUid?.split('-')[2];
        const isLinkoPayMerchant = originalMerchantPart === '1' || originalMerchantPart === '2';
        const isUnpaidLinkoPayCase = isLinkoPayMerchant && lockerData.status === '결제 예정';
        const isLinkPayPaid = isLinkoPayMerchant && !isUnpaidLinkoPayCase;
        const isLinkoPayActive = finalSelectedPaymentOptions.some(opt => opt.name === '링코페이');

        const isTypeChanging =
          (!isLinkPayPaid && !isUnpaidLinkoPayCase && isLinkoPayActive) ||
          isUnpaidLinkoPayCase ||
          (isLinkPayPaid && !isLinkoPayActive);

        (async () => {
          let responseData;

          if (isTypeChanging) {
            const sourceType = isUnpaidLinkoPayCase ? 'EXPECTED' : 'USAGE';
            const targetType = isLinkoPayActive ? 'LINKO_PAY' : 'ON_SITE';
            const requestData = {
              sourceType,
              sourceId: lockerData.usageId,
              storeId,
              memberId,
              lockerId,
              merchantUid: lockerData.merchantUid || '',
              targetType,
              startDate: startDate.format('YYYY-MM-DDT00:00:00'),
              endDate: endDate.format('YYYY-MM-DDT23:59:59'),
              approvalDate: approvalDate.format('YYYY-MM-DDTHH:mm:ss'),
              cardPrice: finalCardPrice,
              cashPrice: finalCashPrice,
              discountPrice: 0,
              point: finalPoint,
              selectedPaymentOptions: finalSelectedPaymentOptions
            };
            responseData = await fetchModule.postDataVersion2('locker/usage/change-payment', 'POST', requestData);
          }
          else {
            const requestData = {
              usageId: lockerData.usageId,
              lockerId,
              startDate: startDate.format('YYYY-MM-DDT00:00:00'),
              endDate: endDate.format('YYYY-MM-DDT23:59:59'),
              approvalDate: approvalDate.format('YYYY-MM-DDTHH:mm:ss'),
              cardPrice: finalCardPrice,
              cashPrice: finalCashPrice,
              discountPrice: 0,
              selectedPaymentOptions: finalSelectedPaymentOptions
            };
            responseData = await fetchModule.postDataVersion2('locker/modify', 'POST', requestData);
          }

          if (responseData.status === 200) {
            saveClick();
          }
          else if (responseData.status === 201) {
            alert(t('main.locker.start.dataNotFound'));
          }
          else if (responseData.status === 202) {
            alert(t('memberDetail.lockerModal.alertDeletedLockerUnavailable'));
          }
          else if (responseData.status === 203) {
            alert(t('memberDetail.lockerModal.alertLockerUnavailable'));
          }
          else if (responseData.status === 204) {
            alert(t('memberDetail.lockerModal.alertLockerInUse'));
          }
          else if (responseData.status === 205) {
            alert(responseData.error);
          }
          else if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/login');
          }
          else {
            alert(responseData.error);
          }
        })();
        return;
      }

      let requestData = {
        registrationType: 'ADD_LOCKER',
        memberId: memberId,
        storeId: storeId,
        approvalDate: approvalDate.format('YYYY-MM-DDTHH:mm:ss'),
        locker: {
          addLocker: true,
          lockerId: lockerId,
          lockerName: lockerData.lockerName,
          lockerCardPrice: finalCardPrice,
          lockerCashPrice: finalCashPrice,
          lockerDiscountPrice: 0,
          lockerPoint: finalPoint,
          lockerStartDate: startDate.format('YYYY-MM-DD') + 'T00:00:00',
          lockerEndDate: endDate.format('YYYY-MM-DD') + 'T23:59:59',
          lockerPaymentOptions: finalSelectedPaymentOptions
        }
      };

      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('member/enroll', 'POST', requestData);

          if (responseData.status === 200) {
            saveClick();
          }
          else if (responseData.status === 201) {
            alert(responseData.error);
          }
          else if (responseData.status === 204) {
            if (responseData.error === 'Locker Already Using') {
              alert(t('main.locker.alreadyInUse'));
            }
            else if (responseData.error === 'Invalid Using') {
              alert(t('main.locker.unavailableLocker'));
            }
            else if (responseData.error === 'Invalid User Data') {
              alert(t('main.locker.invalidMemberInfo'));
            }
          }
          else if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/login');
          }
          else {
            alert(responseData.error);
            return;
          }
        }
      )();
    }

  return (
    <TotalModalWrapper>
      {isClickedAddPaymentOption && <AddPaymentOptionModal onClickSave={onClickSavePaymentOption} onClickExit={() => setIsClickedAddPaymentOption(false)} />}
      <ModalView>
        <TotalWrapper>
          <Title>{t('main.locker.start.title').replace('{locker}', lockerData.lockerName)}</Title>
          <DivisionLine margin={15} />
          <ComponentWrapper>
            <LockerMemberSearchInput
              ref={MemberRef}
              disabled={lockerData.state === 'Expected'}
              onClickMembership={(memberShipStartDate, memberShipEndDate) => {
                setStartDate(moment(memberShipStartDate));
                setEndDate(moment(memberShipEndDate));
              }} />
            <ColDivisionLine />
            <InputWrapper>
              <IndividualWrapper style={{ zIndex: selectedPaymentOptions.length + 3 }}>
                <Text>{t('main.locker.start.startDate')}</Text>
                <CalenderInput ref={StartCalenderRef} value={startDate} setValue={setStartDate} disabled={false} className='StartCalender' clickFn={onClickCalender} placeholder={t('main.locker.start.startDate')} width={130} height={35} />
              </IndividualWrapper>
              <IndividualWrapper style={{ zIndex: selectedPaymentOptions.length + 2 }}>
                <Text>{t('main.locker.endDate')}</Text>
                <CalenderInput ref={EndCalenderRef} value={endDate} setValue={setEndDate} disabled={false} className='EndCalender' clickFn={onClickCalender} placeholder={t('main.locker.endDate')} width={130} height={35} />
              </IndividualWrapper>
              <IndividualWrapper>
                <Text>{t('main.locker.start.appliedDays')}</Text>
                <PaymentUnit>
                  <CountDaysInput value={dayCount} setValue={onSetDayCount} isFull={true} />
                </PaymentUnit>
              </IndividualWrapper>
              <DivisionLine margin={15} />
              <IndividualWrapper style={{ zIndex: selectedPaymentOptions.length + 1, marginBottom: '10px'}}>
                <Text>{t('main.locker.start.paymentDate')}</Text>
                <CalenderInput ref={ApprovalCalenderRef} value={approvalDate} setValue={setApprovalDate} disabled={false} className='ApprovalCalender' clickFn={onClickCalender} placeholder={t('main.locker.start.paymentDate')} width={130} height={35} />
              </IndividualWrapper>
              {
                selectedPaymentOptions.map((data, index) => (
                  <LayerWrapper
                    key={'SelectedPaymentOptions-' + index}
                    marginTop={0}
                    gap={20}
                    style={{ zIndex: selectedPaymentOptions.length - index }}>
                    <SelectOptionInputWrapper>
                      <PaymentOptionTitleWrapper>
                        <CustomText fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('main.locker.paymentMethod') + ' ' + (index === 0 ? '' : index + 1)}</CustomText>
                        {
                          index !== 0 &&
                          <DeleteButton onClick={() => onClickDeletePaymentOptions(index)}>
                            <DeleteIcon src={WhiteTrashIcon} />
                          </DeleteButton>
                        }
                      </PaymentOptionTitleWrapper>
                      <PaymentOptionSettingSelectBox
                        index={index}
                        value={data}
                        setValue={onChangeSelectedPaymentOptions}
                        options={paymentOptions}
                        disabled={paymentOptions.length === 0}
                        width={126}
                        onClickAdditionOption={onClickAdditionOption}
                        onClickDeleteOption={onClickDeleteOption}
                        includeLinkoPayOption={true} />
                    </SelectOptionInputWrapper>
                    <SelectOptionInputWrapper>
                      <PaymentOptionTitleWrapper>
                        <CustomText fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('main.locker.receivedPrice')}</CustomText>
                      </PaymentOptionTitleWrapper>
                      <UnitInputWrapper>
                        <PaymentOptionCustomInput
                          index={index}
                          value={(data.price ?? 0).toLocaleString('ko-KR')}
                          setValue={onChangePaymentOptionPrice}
                          placeholder='0'
                          disabled={false}
                          textAlign='right'
                          width={91} />
                        <CustomText fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('main.locker.currencyUnit')}</CustomText>
                      </UnitInputWrapper>
                    </SelectOptionInputWrapper>
                  </LayerWrapper>
                ))
              }
              <AddPaymentOptionButton onClick={onClickAddPaymentOptions}>{t('main.locker.addPaymentMethod')}</AddPaymentOptionButton>
            </InputWrapper>
          </ComponentWrapper>
          <PaddingWrapper />
          <ButtonWrapper>
            <ExitButton onClick={exitClick}>{t('main.locker.cancel')}</ExitButton>
            {
              isLinkoPaySelected
                ? <LinkoPay onClick={onClickSave}>{t('memberDetail.lockerModal.btnSendLinkoPay')}</LinkoPay>
                : <SaveButton onClick={onClickSave}>{t('main.locker.save')}</SaveButton>
            }
          </ButtonWrapper>
        </TotalWrapper>
      </ModalView>
    </TotalModalWrapper>
  )
})

export default LockerStartModal;

const ModalView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 20px;

  box-sizing: border-box;
  width: 706px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${props => props.theme.subBackground};
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 10px;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 20px;

  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  gap: 10px;

  width: 280px;
`;

const IndividualWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const PaymentUnit = styled.div`
  display: flex;
  flex-direction: row;

  width: 130px;
`;

const LayerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: ${props => props.marginTop}px;

  width: 100%;

  gap: ${props => props.gap}px;
`;

const SelectOptionInputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const UnitInputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const DivisionLine = styled.div`
  margin-top: ${props => props.margin}px;
  margin-bottom: ${props => props.margin}px;

  width: 100%;
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const ColDivisionLine = styled.div`
  align-self: stretch;

  margin: 15px 0;

  border-left: 1px solid ${props => props.theme.line};
`;

const AddPaymentOptionButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 10px;

  width: 100%;
  height: 40px;

  border-radius: 20px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Bold;
  font-size: 14px;

  color: ${props => props.theme.line};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      color: ${props => props.theme.subBackground};
      background-color: ${props => props.theme.subKey};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border-radius: 2px;
  background-color: ${props => props.theme.pointRedAlpha20};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }

  transition: all 0.3s ease-in-out;
`;

const DeleteIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const PaymentOptionTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const Text = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const PaddingWrapper = styled.div`
  height: 27px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 80px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const LinkoPay = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.subKey};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CustomText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  color: ${props => props.color};

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }

  transition: all 0.3s ease-in-out;
`;